import { h } from 'preact';
import {
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
} from 'preact/hooks';
import { debounce } from 'helpers/debounce';
import produce from 'immer';
import classNames from 'classnames';
import SendIcon from 'widget_main/globals/assets/send.svg';
import Button from 'widget_main/components/ui/Button';
import TextArea from 'widget_main/components/ui/TextArea';
import Text from 'widget_main/components/Text';
import { Localizer } from 'preact-i18n';
import { getKustomerCore } from 'widget_main/globals/helpers';
import { Attachment } from 'core_main/sdk/send_message/types';
import { MessageThreadInputProps, MessageThreadInputState } from './types';
import AttachmentPreviews from './AttachmentPreviews';
import AttachmentButton from './AttachmentButton';
import styles from './messageThreadInput.scss';
import messages from './messages';
import EmojiPicker from './EmojiPicker/EmojiPicker';

const TEXT_AREA_ID = 'messageThreadInputTextArea';

const kustomerCore = getKustomerCore();

function MessageThreadInput({
  isInputHidden,
  isSendDisabled,
  className,
  chatAvailability,
  conversationId,
  draft,
  autoFocusOnVisible = true,
  onClick,
  onSubmit,
  showAttachmentButton,
}: MessageThreadInputProps) {
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState<
    MessageThreadInputState['attachments']
  >([]);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );
  const [pendingCallback, setPendingCallback] = useState<(() => void) | null>(
    null,
  );
  const [pendingCursorPosition, setPendingCursorPosition] = useState<
    number | null
  >(null);

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const focusOnTextArea = (cursorLocation?: number) => {
    const element =
      textAreaRef.current || document.getElementById(TEXT_AREA_ID);

    if (element) {
      element.focus?.();
      if (cursorLocation !== undefined) {
        setPendingCursorPosition(cursorLocation);
      }
    }
  };

  // focus when input becomes visible and autoFocusOnVisible is enabled
  useEffect(() => {
    if (isInputHidden === false && autoFocusOnVisible) {
      focusOnTextArea();
    }
  }, [isInputHidden, autoFocusOnVisible]);

  // Handle pending callbacks and cursor positioning after resize
  useLayoutEffect(() => {
    if (pendingCallback) {
      pendingCallback();
      setPendingCallback(null);
    }

    if (pendingCursorPosition !== null && textAreaRef.current) {
      textAreaRef.current.selectionStart = pendingCursorPosition;
      textAreaRef.current.selectionEnd = pendingCursorPosition;
      setPendingCursorPosition(null);
    }
  }, [pendingCallback, pendingCursorPosition]);

  // on unmount, revoke object urls
  useEffect(() => {
    return () => {
      attachments.forEach((attachment) => {
        if (attachment.url) URL.revokeObjectURL(attachment.url);
      });
    };
  }, []);

  const showError = (errorMessage: string | undefined) => {
    setErrorMessage(errorMessage);
  };

  const addAttachment = (file: File) => {
    const previewUrl = URL.createObjectURL(file);
    setAttachments((prevAttachments) =>
      produce(prevAttachments, (draftState) => {
        draftState.push({ file, url: previewUrl });
      }),
    );
  };

  const removeAttachment = useCallback((removedAttachment: Attachment) => {
    if (removedAttachment.url) URL.revokeObjectURL(removedAttachment.url);
    setAttachments((prevAttachments) =>
      produce(prevAttachments, (draftState) => {
        return draftState.filter(
          (attachment) => attachment.file !== removedAttachment.file,
        );
      }),
    );
  }, []);

  const debouncedUserTyping = useMemo(
    () =>
      debounce(
        () => {
          if (!conversationId) return;
          kustomerCore?.sendTypingActivity({ conversationId, typing: true });
        },
        1000,
        {
          leading: true,
          maxWait: 2500,
        },
      ),
    [conversationId],
  );

  const debouncedUserStoppedTyping = useMemo(
    () =>
      debounce(() => {
        if (!conversationId) return;
        kustomerCore?.sendTypingActivity({ conversationId, typing: false });
      }, 5000),
    [conversationId],
  );

  const resizeTextarea = () => {
    // We are manipulating the dom directly here, which is frowned upon, but I couldn't get it working any other way.
    // If the person reading this in the future (even my future self) has a better way to do this, then by all means I
    // won't be insulted if you change this code.
    if (textAreaRef.current?.style) {
      // If body is empty, reset the styles
      if (!body) {
        textAreaRef.current.style.height = '';
        textAreaRef.current.style.overflow = '';
        return;
      }
      // We need to set it to 0 first in order to properly measure the scrollHeight, otherwise when a user DELETES text
      // the textarea will not shrink.
      textAreaRef.current.style.height = '0px';
      textAreaRef.current.style.height = `${textAreaRef.current?.scrollHeight}px`;
      textAreaRef.current.style.overflow = 'auto';
    }
  };

  // Handle textarea resizing after body changes
  useLayoutEffect(() => {
    resizeTextarea();
  }, [body]);

  const handleChange = (e: any, cb?: () => void) => {
    const newBody = e.target.value;

    if (conversationId) {
      debouncedUserTyping();
      debouncedUserStoppedTyping();
    }

    setBody(newBody);

    if (cb) {
      setPendingCallback(cb);
    }
  };

  const handleSubmit = () => {
    if (isSendDisabled) return;

    if (body || attachments.length) {
      onSubmit({
        body,
        attachments,
      });

      setBody('');
      setAttachments([]);
      setErrorMessage(undefined);
    }
  };

  const handleEmoji = (emoji: string) => {
    const textAreaElement = textAreaRef.current;
    const selectionStart = textAreaElement?.selectionStart ?? 0;
    const selectionEnd = textAreaElement?.selectionEnd ?? 0;
    const currentInputValue = body;
    // insert emoji at place of cursor
    const newValue =
      currentInputValue.slice(0, selectionStart) +
      emoji +
      currentInputValue.slice(selectionEnd);
    handleChange({ target: { value: newValue } }, () => {
      focusOnTextArea(selectionStart + emoji.length); // emojis can have different char lengths 🤯
    });
  };

  const renderAttachmentsButton = () => {
    return showAttachmentButton ? (
      <div>
        <AttachmentButton
          addAttachment={addAttachment}
          attachments={attachments}
          showError={showError}
        />
      </div>
    ) : null;
  };

  const renderAttachmentsPreview = () => {
    if (!attachments.length) {
      return null;
    }

    return (
      <AttachmentPreviews
        attachments={attachments}
        removeAttachment={removeAttachment}
      />
    );
  };

  // Handle draft message using useEffect
  useEffect(() => {
    if (draft?.message) {
      setBody(draft.message || '');
      if (typeof draft?.callback === 'function') {
        draft.callback();
      }
    }
  }, [draft]);

  const placeholder = (chatAvailability === 'offline'
    ? Text({
        id: 'messageThreadInputLeaveAMessage',
        defaultMessage: messages.messageThreadInputLeaveAMessage,
      })
    : Text({
        id: 'messageThreadInputSendAMessage',
        defaultMessage: messages.messageThreadInputSendAMessage,
      })) as unknown as string;

  return (
    <div
      class={classNames(
        styles.messageInput,
        {
          [styles.hasError]: !!errorMessage,
          [styles.isInputHidden]: isInputHidden,
        },
        className,
      )}
    >
      {renderAttachmentsPreview()}
      <div className={styles.lowerSection}>
        {renderAttachmentsButton()}
        <div>
          <EmojiPicker onEmoji={handleEmoji} onClose={focusOnTextArea} />
        </div>
        <Localizer>
          <TextArea
            ref={textAreaRef}
            id={TEXT_AREA_ID}
            className={styles.textArea}
            ariaLabel={placeholder}
            placeholder={errorMessage || placeholder}
            onChange={handleChange}
            onClick={onClick}
            onEnter={handleSubmit}
            value={body}
          />
        </Localizer>
        <div>
          <Localizer>
            <Button
              variant="primary"
              className={styles.submitButton}
              ariaLabel={Text({
                id: 'submitButtonAriaLabel',
                defaultMessage: messages.submitButtonAriaLabel,
              })}
              onClick={handleSubmit}
              disabled={(!body && !attachments.length) || isSendDisabled}
            >
              <SendIcon className={styles.sendIcon} />
            </Button>
          </Localizer>
        </div>
      </div>
    </div>
  );
}

export default MessageThreadInput;
